import { create } from 'zustand';
import { Organizer } from '../types/organizer';
import { 
  createOrganizerProfile, 
  getAllOrganizers,
  updateOrganizerProfile,
  deleteOrganizerProfile,
  resetOrganizerPassword
} from '../lib/firebase/database/organizers';
import { collection, getDocs, query, where, writeBatch, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import toast from 'react-hot-toast';
import { createUserWithoutSignIn } from '../lib/firebase/auth';

type OrganizersState = {
  organizers: Record<string, Organizer>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchOrganizers: () => Promise<void>;
  createOrganizer: (organizer: Organizer) => Promise<void>;
  updateOrganizer: (organizerId: string, updates: Partial<Organizer>) => Promise<void>;
  deleteOrganizer: (organizerId: string) => Promise<void>;
  resetPassword: (organizerId: string) => Promise<string>;
  setPassword: (organizerId: string, newPassword: string) => Promise<void>;
  syncOrganizerName: (organizerId: string) => Promise<{ updated: number }>; // New function
  replaceAuthCredentials: (organizerId: string, newEmail: string, newPassword: string) => Promise<void>;
};

export const useOrganizersStore = create<OrganizersState>((set, get) => ({
  organizers: {},
  loading: false,
  error: null,
  initialized: false,

  fetchOrganizers: async () => {
    // Skip if already initialized
    if (get().initialized && Object.keys(get().organizers).length > 0) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const organizers = await getAllOrganizers();
      set({ organizers, loading: false, initialized: true });
    } catch (error: any) {
      console.error('Error fetching organizers:', error);
      set({ error: error.message, loading: false });
    }
  },

  createOrganizer: async (organizer: Organizer) => {
    set({ loading: true, error: null });
    try {
      const newOrganizer = await createOrganizerProfile(organizer);
      set((state) => ({
        organizers: {
          ...state.organizers,
          [organizer.id]: newOrganizer
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error creating organizer:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateOrganizer: async (organizerId: string, updates: Partial<Organizer>) => {
    set({ loading: true, error: null });
    try {
      // Get current organizer data
      const currentOrganizer = get().organizers[organizerId];
      if (!currentOrganizer) {
        throw new Error('Organizer not found');
      }

      // If name is being updated, we'll need to update related collections
      const isNameUpdated = updates.name && updates.name !== currentOrganizer.name;
      
      // First, update the organizer profile
      await updateOrganizerProfile(organizerId, updates);
      
      // If name was updated, initiate a background sync but don't wait for it
      if (isNameUpdated) {
        // This will start the sync process without blocking the UI update
        get().syncOrganizerName(organizerId)
          .then(result => {
            console.log(`Name sync completed: ${result.updated} records updated`);
          })
          .catch(error => {
            console.error('Error in background sync:', error);
          });
      }

      set((state) => ({
        organizers: {
          ...state.organizers,
          [organizerId]: { ...state.organizers[organizerId], ...updates }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating organizer:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  syncOrganizerName: async (organizerId: string) => {
    set({ loading: true, error: null });
    try {
      // Get current organizer data
      const organizer = get().organizers[organizerId];
      if (!organizer?.name) {
        throw new Error('Organizer or organizer name not found');
      }

      console.log(`Starting sync for organizer ${organizerId} (${organizer.name})`);
      
      let totalUpdated = 0;
      const batchSize = 450; // Firestore batch limit is 500
      
      // Update guides first
      const guidesRef = collection(firestore, 'Guides');
      const guidesQuery = query(guidesRef, where('organizerId', '==', organizerId));
      const guidesSnapshot = await getDocs(guidesQuery);
      
      if (!guidesSnapshot.empty) {
        console.log(`Found ${guidesSnapshot.size} guides to update`);
        let batch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const guideDoc of guidesSnapshot.docs) {
          batch.update(guideDoc.ref, { organizerName: organizer.name });
          batchCount++;
          
          if (batchCount >= batchSize) {
            await batch.commit();
            totalUpdated += batchCount;
            batch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }
      
      // Update PilgrimGuideLinks (using multiple batches if needed)
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksQuery = query(linksRef, where('organizerId', '==', organizerId));
      const linksSnapshot = await getDocs(linksQuery);
      
      if (!linksSnapshot.empty) {
        console.log(`Found ${linksSnapshot.size} links to update`);
        let batch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const linkDoc of linksSnapshot.docs) {
          batch.update(linkDoc.ref, { 
            organizerName: organizer.name,
            updatedAt: Date.now()
          });
          batchCount++;
          
          if (batchCount >= batchSize) {
            await batch.commit();
            totalUpdated += batchCount;
            batch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }
      
      // Update PilgrimSelector
      const selectorRef = collection(firestore, 'PilgrimSelector');
      const selectorQuery = query(selectorRef, where('organizerId', '==', organizerId));
      const selectorSnapshot = await getDocs(selectorQuery);
      
      if (!selectorSnapshot.empty) {
        console.log(`Found ${selectorSnapshot.size} PilgrimSelector documents to update`);
        let batch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const selectorDoc of selectorSnapshot.docs) {
          batch.update(selectorDoc.ref, { 
            'organizerData.name': organizer.name,
            updatedAt: Date.now()
          });
          batchCount++;
          
          if (batchCount >= batchSize) {
            await batch.commit();
            totalUpdated += batchCount;
            batch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }
      
      // Update operation list if it exists
      const operationRef = collection(firestore, 'operationlist');
      const operationQuery = query(operationRef, where('organizerId', '==', organizerId));
      const operationSnapshot = await getDocs(operationQuery);
      
      if (!operationSnapshot.empty) {
        console.log(`Found ${operationSnapshot.size} operation list entries to update`);
        let batch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const opDoc of operationSnapshot.docs) {
          batch.update(opDoc.ref, { 
            organizerName: organizer.name,
            updatedAt: Date.now()
          });
          batchCount++;
          
          if (batchCount >= batchSize) {
            await batch.commit();
            totalUpdated += batchCount;
            batch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
        }
      }

      console.log(`Sync completed: ${totalUpdated} documents updated`);
      toast.success(`Updated ${totalUpdated} references to organizer name`);

      set({ loading: false });
      return { updated: totalUpdated };
    } catch (error: any) {
      console.error('Error syncing organizer name:', error);
      set({ error: error.message, loading: false });
      toast.error('Failed to sync organizer name');
      throw error;
    }
  },

  replaceAuthCredentials: async (organizerId: string, newEmail: string, newPassword: string) => {
    set({ loading: true, error: null });
    try {
      console.log(`Starting auth credentials replacement for organizer ${organizerId}`);
      
      // 1. Create new Firebase Auth user
      const firebaseUser = await createUserWithoutSignIn(newEmail, newPassword);
      const newOrgId = firebaseUser.uid;
      
      // 2. Get current organizer data
      const organizer = get().organizers[organizerId];
      if (!organizer) {
        throw new Error('Organizer not found');
      }
      
      // 3. Create new organizer document with the new ID
      const newOrganizer: Organizer = {
        ...organizer,
        id: newOrgId,
        email: newEmail,
        password: newPassword,
        updatedAt: Date.now()
      };
      
      const organizerRef = doc(firestore, 'Organizers', newOrgId);
      await setDoc(organizerRef, newOrganizer);
      
      // 4. Update all references to the organizer ID
      const batch = writeBatch(firestore);
      const batchSize = 450; // Firestore batch limit is 500
      
      // 4.1. Update guides
      const guidesRef = collection(firestore, 'Guides');
      const guidesQuery = query(guidesRef, where('organizerId', '==', organizerId));
      const guidesSnapshot = await getDocs(guidesQuery);
      
      let totalBatches = 0;
      let totalUpdated = 0;
      
      if (!guidesSnapshot.empty) {
        console.log(`Found ${guidesSnapshot.size} guides to update`);
        
        // Split into multiple batches if needed
        let currentBatch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const guideDoc of guidesSnapshot.docs) {
          currentBatch.update(guideDoc.ref, { 
            organizerId: newOrgId,
            updatedAt: Date.now()
          });
          
          batchCount++;
          
          if (batchCount >= batchSize) {
            await currentBatch.commit();
            totalBatches++;
            totalUpdated += batchCount;
            console.log(`Committed batch ${totalBatches} with ${batchCount} guides`);
            
            currentBatch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        // Commit any remaining guides
        if (batchCount > 0) {
          await currentBatch.commit();
          totalBatches++;
          totalUpdated += batchCount;
          console.log(`Committed final batch ${totalBatches} with ${batchCount} guides`);
        }
      }
      
      // 4.2. Update PilgrimGuideLinks
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksQuery = query(linksRef, where('organizerId', '==', organizerId));
      const linksSnapshot = await getDocs(linksQuery);
      
      if (!linksSnapshot.empty) {
        console.log(`Found ${linksSnapshot.size} links to update`);
        
        // Split into multiple batches
        let currentBatch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const linkDoc of linksSnapshot.docs) {
          currentBatch.update(linkDoc.ref, { 
            organizerId: newOrgId,
            updatedAt: Date.now()
          });
          
          batchCount++;
          
          if (batchCount >= batchSize) {
            await currentBatch.commit();
            totalBatches++;
            totalUpdated += batchCount;
            console.log(`Committed batch ${totalBatches} with ${batchCount} links`);
            
            currentBatch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        // Commit any remaining links
        if (batchCount > 0) {
          await currentBatch.commit();
          totalBatches++;
          totalUpdated += batchCount;
          console.log(`Committed final batch ${totalBatches} with ${batchCount} links`);
        }
      }
      
      // 4.3. Update PilgrimSelector
      const selectorRef = collection(firestore, 'PilgrimSelector');
      const selectorQuery = query(selectorRef, where('organizerId', '==', organizerId));
      const selectorSnapshot = await getDocs(selectorQuery);
      
      if (!selectorSnapshot.empty) {
        console.log(`Found ${selectorSnapshot.size} selectors to update`);
        
        // Split into multiple batches
        let currentBatch = writeBatch(firestore);
        let batchCount = 0;
        
        for (const selectorDoc of selectorSnapshot.docs) {
          currentBatch.update(selectorDoc.ref, { 
            organizerId: newOrgId,
            updatedAt: Date.now()
          });
          
          batchCount++;
          
          if (batchCount >= batchSize) {
            await currentBatch.commit();
            totalBatches++;
            totalUpdated += batchCount;
            console.log(`Committed batch ${totalBatches} with ${batchCount} selectors`);
            
            currentBatch = writeBatch(firestore);
            batchCount = 0;
          }
        }
        
        // Commit any remaining selectors
        if (batchCount > 0) {
          await currentBatch.commit();
          totalBatches++;
          totalUpdated += batchCount;
          console.log(`Committed final batch ${totalBatches} with ${batchCount} selectors`);
        }
      }

      // 5. Delete the old organizer document
      await deleteOrganizerProfile(organizerId);
      
      // 6. Update local state
      set(state => {
        const newOrganizers = { ...state.organizers };
        delete newOrganizers[organizerId];
        newOrganizers[newOrgId] = newOrganizer;
        
        return { 
          organizers: newOrganizers,
          loading: false
        };
      });
      
      console.log(`Auth credentials replacement completed successfully. Updated ${totalUpdated} documents in ${totalBatches} batches.`);
      return;
    } catch (error: any) {
      console.error('Error replacing auth credentials:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteOrganizer: async (organizerId: string) => {
    set({ loading: true, error: null });
    try {
      await deleteOrganizerProfile(organizerId);
      
      set((state) => {
        const newOrganizers = { ...state.organizers };
        delete newOrganizers[organizerId];
        return { organizers: newOrganizers, loading: false };
      });
    } catch (error: any) {
      console.error('Error deleting organizer:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  resetPassword: async (organizerId: string) => {
    set({ loading: true, error: null });
    try {
      const newPassword = Math.random().toString(36).slice(-8);
      await resetOrganizerPassword(organizerId, newPassword);
      
      // Update local state
      set((state) => ({
        organizers: {
          ...state.organizers,
          [organizerId]: {
            ...state.organizers[organizerId],
            password: newPassword,
            updatedAt: Date.now()
          }
        },
        loading: false
      }));

      return newPassword;
    } catch (error: any) {
      console.error('Error resetting password:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  setPassword: async (organizerId: string, newPassword: string) => {
    set({ loading: true, error: null });
    try {
      await resetOrganizerPassword(organizerId, newPassword);

      // Update local state
      set((state) => ({
        organizers: {
          ...state.organizers,
          [organizerId]: {
            ...state.organizers[organizerId],
            password: newPassword,
            updatedAt: Date.now()
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error setting organizer password:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  }
}));
